import { useId } from "react";

import styles from "./number-field.module.css";
import { RequiredMark } from "./required-mark";

/**
 * Every number a person types in DentaCore (QA20-13, 2026-09-20).
 *
 * ── The bug this exists to end ──────────────────────────────────────────────
 *
 * The client typed `2e` into a branch's turnover. In Chromium and Edge — which
 * is what he was using — `<input type="number">` accepts those two characters,
 * shows them, and then reports **`value === ""`** with `validity.badInput` set.
 * So the field said `2e` and the application read *nothing*, took that as "no
 * turnover given", and silently fell back to the default. Native validation
 * happened to stop the submit here; a form that reads the value before
 * submitting, or one whose control is a button rather than a submit, would have
 * written the wrong number without a word.
 *
 * Nine files had their own `type="number"`. Hardening them in place would have
 * been nine copies to drift and a tenth input next month with none of it.
 *
 * ── Why this is not `type="number"` at all ──────────────────────────────────
 *
 * Because the hole is in the type, not in how it was used. `type="number"` is
 * allowed to hide what it cannot parse, and no amount of `min`, `max` or
 * `step` changes that — the element's contract is *"a number or nothing"*,
 * and "nothing" is indistinguishable from an empty field.
 *
 * `type="text"` with `inputMode` gives back the one thing that matters: **the
 * value is always exactly what is on screen**. Phones still show a numeric
 * keypad, because `inputMode` is what decides that, not `type`. What is lost is
 * the spinner arrows, which nobody uses for a price.
 *
 * ── The rule about what it refuses ──────────────────────────────────────────
 *
 * Characters that cannot survive are refused **as they are typed**, so the
 * field never holds something that will be reinterpreted later. Values that are
 * merely out of range are *kept* and reported — a half-typed `1` on the way to
 * `15` is out of range for a moment, and a field that deleted it would be
 * unusable.
 */

export interface NumberFieldRules {
  /** Inclusive. Omitted means unbounded, and a leading `-` is then allowed. */
  min?: number;
  /** Inclusive. */
  max?: number;
  /** Whole numbers only — refuses the decimal point rather than rounding later. */
  integer?: boolean;
  /** What the number is, for the refusal sentence: "minutes", "days", "units". */
  unit?: string;
}

/**
 * Strip what cannot be part of a number, keeping everything that can.
 *
 * Deliberately permissive about *order* — `-` and `.` are allowed only once and
 * only where they make sense, but a partially-typed value like `-` or `1.` is
 * left alone, because a person typing `-1.5` passes through both. `describe`
 * below is what refuses those; this only removes characters that could never
 * appear in any completed value.
 */
export function sanitiseNumeric(raw: string, rules: NumberFieldRules = {}): string {
  const allowSign = rules.min === undefined || rules.min < 0;
  const allowPoint = rules.integer !== true;

  let out = "";
  for (const char of raw) {
    if (char >= "0" && char <= "9") {
      out += char;
      continue;
    }
    // A sign only at the very front, and only when negatives are possible.
    if (char === "-" && allowSign && out.length === 0) {
      out += char;
      continue;
    }
    // One point, and never as the first character — `.5` round-trips to `0.5`
    // through `Number`, which is a silent rewrite of what was typed.
    if (char === "." && allowPoint && out.length > 0 && !out.includes(".")) {
      out += char;
      continue;
    }
    // Everything else — `e`, `E`, `+`, a second point, spaces, letters — is
    // dropped. This is the whole of the `2e` bug: those characters used to be
    // accepted and then swallowed the value with them.
  }
  return out;
}

/**
 * What is wrong with this value, in a sentence, or `null` if nothing is.
 *
 * **Exported, because the form owns the submit button.** A field cannot know
 * whether the form it sits in should be submittable, so callers ask this and
 * disable accordingly — which is also what stops a disabled-button form silently
 * accepting a bad value the way the turnover field did.
 */
export function describeNumberError(value: string, rules: NumberFieldRules = {}): string | null {
  const trimmed = value.trim();
  if (trimmed === "") return null; // Empty is the caller's business, not ours.

  // `-` and `1.` on the way to a real number. Incomplete rather than wrong, but
  // not submittable either.
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return "That is not a complete number.";

  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return "That is not a number.";

  const unit = rules.unit === undefined ? "" : ` ${rules.unit}`;

  if (rules.integer === true && !Number.isInteger(parsed)) {
    return `Whole${unit === "" ? " numbers" : unit} only.`;
  }
  if (rules.min !== undefined && parsed < rules.min) {
    return `Must be at least ${String(rules.min)}${unit}.`;
  }
  if (rules.max !== undefined && parsed > rules.max) {
    return `Must be ${String(rules.max)}${unit} or less.`;
  }
  return null;
}

export function NumberField({
  label,
  value,
  onChange,
  rules = {},
  disabled,
  placeholder,
  suffix,
  hint,
  testId,
  className,
  labelClassName,
  inputClassName,
  autoFocus,
  required,
}: {
  label: string;
  /**
   * **A string, not a number.** What is on screen is the truth — `12` and `12.`
   * and `012` are different things to somebody mid-keystroke, and a `number`
   * prop would force the field to rewrite them under the cursor.
   */
  value: string;
  onChange: (next: string) => void;
  rules?: NumberFieldRules;
  disabled?: boolean;
  placeholder?: string;
  /** Drawn inside the field's trailing edge — "minutes", "%", a currency. */
  suffix?: string;
  /** A sentence under the field, shown while there is nothing to complain about. */
  hint?: string;
  testId?: string;
  /**
   * The three class overrides exist so a call site keeps the shape it already
   * had.
   *
   * Nine screens each had their own `type="number"` and their own wrapper, and
   * a component that imposed one look would have made converting them a visual
   * change as well as a behavioural one — which is the surest way to have a
   * conversion rejected, or done to two of the nine. What has to be identical
   * is what the field *accepts*; what it looks like is the screen's business.
   */
  className?: string;
  labelClassName?: string;
  inputClassName?: string;
  autoFocus?: boolean;
  /** Draws the red star (see `ui/required-mark.tsx`). */
  required?: boolean;
}): React.JSX.Element {
  const id = useId();
  const error = describeNumberError(value, rules);
  const describedBy =
    error !== null ? `${id}-error` : hint !== undefined ? `${id}-hint` : undefined;

  return (
    <label className={className ?? styles.field} htmlFor={id}>
      <span className={labelClassName ?? styles.label}>
        {label}
        {required === true && <RequiredMark />}
      </span>

      <span className={styles.control}>
        <input
          id={id}
          /**
           * **`text`, not `number`** — see the note at the top of this file.
           * `inputMode` is what raises a numeric keypad on a phone, so nothing
           * is lost by it.
           */
          type="text"
          inputMode={rules.integer === true ? "numeric" : "decimal"}
          // Off: a browser offering last month's turnover as a completion for a
          // price is worse than no help at all.
          autoComplete="off"
          className={inputClassName ?? styles.input}
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          autoFocus={autoFocus}
          aria-invalid={error !== null}
          aria-describedby={describedBy}
          data-testid={testId}
          onChange={(event) => onChange(sanitiseNumeric(event.target.value, rules))}
          /**
           * **Paste goes through the same filter.** Without this, pasting
           * `1e5` from a spreadsheet lands the characters the typing path
           * refuses — the `onChange` above catches it, and this is here so the
           * caret does not jump when it does.
           */
          onPaste={(event) => {
            const pasted = event.clipboardData.getData("text");
            const cleaned = sanitiseNumeric(pasted, rules);
            if (cleaned !== pasted) {
              event.preventDefault();
              onChange(sanitiseNumeric(value + cleaned, rules));
            }
          }}
        />
        {suffix !== undefined && <span className={styles.suffix}>{suffix}</span>}
      </span>

      {/*
        The reason, in the layout, rather than in a native validation bubble —
        which appears only on submit, points at one field at a time, and cannot
        be read by a screen reader that is not focused on it.
      */}
      {error !== null ? (
        <span className={styles.error} id={`${id}-error`} role="alert">
          {error}
        </span>
      ) : (
        hint !== undefined && (
          <span className={styles.hint} id={`${id}-hint`}>
            {hint}
          </span>
        )
      )}
    </label>
  );
}
